/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    constructor() {
      super();
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.results = document.getElementById('filter-results');

      if ((!this.filteringEnabled && !this.sortingEnabled) || !this.results) return;
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.resultsCountEl = document.getElementById('results-count');
      this.form = document.getElementById('facets');

      if (this.filteringEnabled) {
        this.showFiltersBtn = document.querySelector('.js-show-filters');
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = document.querySelector('.active-filters');
        this.footer = this.querySelector('.facets__footer');
        this.filtersOpen = this.dataset.filtersOpen;
        this.productsJsonUrl = this.dataset.productsJsonUrl;
        this.stockProducts = null;
        this.stockProductsPromise = null;
        this.stockCountsRequestId = 0;

        this.showFiltersBtn.addEventListener('click', this.showFilters.bind(this));
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', window.debounce(this.handleFilterChange.bind(this), 300));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));

        this.updateStockAwareCounts();
      }

      if (this.sortingEnabled) {
        this.desktopSortBy = document.getElementById('sort-by');
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }

      window.addEventListener('popstate', this.handleHistoryChange.bind(this));
      window.addEventListener('on:debounced-resize', this.handleWindowResize.bind(this));

      this.handleWindowResize();
    }

    /**
     * Opens the filters drawer.
     */
    showFilters() {
      this.open(this.showFiltersBtn);
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData);
      const emptyParams = [];

      if (this.sortingEnabled) {
        // Sync selected values of mobile and desktop 'sort by' dropdowns.
        if (evt.target === this.desktopSortBy) {
          const mobileSortBy = this.querySelector(`#drawer-sort-by input[name="sort_by"][value="${this.desktopSortBy.value}"]`);
          if (mobileSortBy) {
            mobileSortBy.checked = true;
          }
        } else if (evt.target.name === 'sort_by') {
          this.desktopSortBy.value = evt.target.value;
        }

        // Remove duplicate 'sort_by' parameters.
        searchParams.set('sort_by', this.desktopSortBy.value);
      }

      // Get empty parameters.
      searchParams.forEach((value, key) => {
        if (!value) emptyParams.push(key);
      });

      // Remove empty parameters.
      emptyParams.forEach((key) => {
        searchParams.delete(key);
      });

      this.applyFilters(searchParams.toString());
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString());
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString());
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      let searchParams = '';

      if (evt.state && evt.state.searchParams) {
        ({ searchParams } = evt.state);
      }

      this.applyFilters(searchParams, false);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleWindowResize() {
      if (!theme.mediaMatches.md) {
        this.querySelectorAll('.filter').forEach((el) => {
          el.open = false;
          el.classList.remove('is-open');
        });
      } else {
        this.querySelectorAll('.filter').forEach((el, index) => {
          if (this.filtersOpen === 'all' || (this.filtersOpen === 'some' && index < 5)) {
            el.open = true;
            el.classList.add('is-open');
          }
        });
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, updateUrl = true) {
      this.results.classList.add('is-loading');

      // Fetch filtered products markup.
      const response = await fetch(`${window.location.pathname}?${searchParams}`);

      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        // Update the filters.
        if (this.filteringEnabled) this.updateFilters(tmpl.content);

        // Update the results.
        this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

        // Update the URL.
        if (updateUrl) FacetFilters.updateURL(searchParams);
      }

      this.results.classList.remove('is-loading');
    }

    /**
     * Updates the filters with the fetched data.
     * @param {string} html - HTML of the fetched document.
     */
    updateFilters(html) {
      // Save current focus
      const currentFocusId = document.activeElement ? document.activeElement.id : null;

      // Save open filters
      const openedFilterIDs = [];
      this.querySelectorAll('.filter[open]').forEach((el) => openedFilterIDs.push(el.id));

      // Update filters
      this.filters.innerHTML = html.querySelector('.facets__filters').innerHTML;

      // Restore open filters
      openedFilterIDs.forEach((id) => {
        this.querySelectorAll(`#${CSS.escape(id)}`).forEach((el) => { el.open = true; });
      });

      // Preserve focus
      if (currentFocusId) {
        const focusEl = this.filters.querySelector(`#${CSS.escape(currentFocusId)}`);
        if (focusEl) focusEl.focus();
      }

      // Update total
      this.updateResultsTotal(html);

      // Update active filters.
      this.updateActiveFilters(html);

      // Update '[x] results' button (mobile only).
      const footerEl = html.querySelector('.facets__footer');
      this.footer.innerHTML = footerEl.innerHTML;

      // Refresh stock-aware counts for the newly rendered filter values.
      this.updateStockAwareCounts();
    }

    /**
     * Recomputes in-stock-aware counts for each variant-option filter
     * checkbox (size/color/etc.) and swaps them in, without changing which
     * products are actually displayed. Sourced from the collection's public
     * products.json (fetched once, then cached), so it doesn't depend on a
     * native Availability filter being configured in Search & Discovery.
     * No-ops on the search page or if that fetch fails.
     */
    async updateStockAwareCounts() {
      if (!this.productsJsonUrl) return;

      const requestId = ++this.stockCountsRequestId;
      const products = await this.getStockProducts();
      if (!products || requestId !== this.stockCountsRequestId) return;

      const activeByParam = this.getActiveFilterParams();

      const countEls = this.filters.querySelectorAll('[data-filter-count]');
      countEls.forEach((el) => {
        const { filterParam, filterValue } = el.dataset;
        let count = 0;
        products.forEach((product) => {
          if (!this.productMatchesOtherFilters(product, activeByParam, filterParam)) return;
          count += product.variants.filter((variant) => (
            variant.available && [variant.option1, variant.option2, variant.option3].includes(filterValue)
          )).length;
        });
        el.textContent = `(${count})`;
      });

      this.updateResultsCountText(products, activeByParam);
    }

    /**
     * Swaps the leading number in the '[x] products/results' header for the
     * real count of cards that would render given the active filters and
     * stock - matching this theme's one-card-per-color-variant grid, not
     * Shopify's native product-level (and stock-unaware) paginate.items.
     * Only the digits are replaced, so the surrounding translated text
     * (singular/plural wording, locale) is left as Shopify rendered it.
     */
    updateResultsCountText(products, activeByParam) {
      if (!this.resultsCountEl) return;

      const count = this.countVisibleCards(products, activeByParam);
      const { textContent } = this.resultsCountEl;
      const updated = textContent.replace(/\d+/, String(count));
      if (updated !== textContent) this.resultsCountEl.textContent = updated;
    }

    /**
     * Counts how many product cards would actually render: one per unique
     * option1 value (this theme's swatch-card key, typically Color) per
     * product, requiring an in-stock variant that satisfies every active
     * filter group at once - mirrors the matching logic in
     * sections/main-collection.liquid so this stays consistent with what's
     * actually in the grid.
     */
    countVisibleCards(products, activeByParam) {
      const gte = activeByParam['filter.v.price.gte'] ? parseFloat(activeByParam['filter.v.price.gte'][0]) : null;
      const lte = activeByParam['filter.v.price.lte'] ? parseFloat(activeByParam['filter.v.price.lte'][0]) : null;

      const productLevelMatch = (product) => {
        if (activeByParam['filter.p.product_type'] && !activeByParam['filter.p.product_type'].includes(product.product_type)) return false;
        if (activeByParam['filter.p.vendor'] && !activeByParam['filter.p.vendor'].includes(product.vendor)) return false;
        if (activeByParam['filter.p.tag'] && !activeByParam['filter.p.tag'].some((tag) => (product.tags || []).includes(tag))) return false;
        return true;
      };

      const variantLevelMatch = (variant) => {
        if (!variant.available) return false;
        const price = parseFloat(variant.price);
        if (gte !== null && price < gte) return false;
        if (lte !== null && price > lte) return false;
        return Object.keys(activeByParam).every((param) => {
          if (!param.startsWith('filter.v.option.')) return true;
          return activeByParam[param].some((value) => [variant.option1, variant.option2, variant.option3].includes(value));
        });
      };

      let count = 0;
      products.forEach((product) => {
        if (!productLevelMatch(product)) return;

        const seenCardKeys = new Set();
        product.variants.forEach((variant) => {
          if (!variantLevelMatch(variant)) return;
          const cardKey = variant.option1 || '';
          if (seenCardKeys.has(cardKey)) return;
          seenCardKeys.add(cardKey);
          count += 1;
        });
      });

      return count;
    }

    /**
     * Fetches (and caches) every product in the collection via products.json,
     * so counts can be recomputed client-side on every filter change without
     * a further request.
     */
    async getStockProducts() {
      if (this.stockProducts) return this.stockProducts;
      if (!this.stockProductsPromise) this.stockProductsPromise = this.fetchAllStockProducts();
      this.stockProducts = await this.stockProductsPromise;
      return this.stockProducts;
    }

    async fetchAllStockProducts() {
      const products = [];

      try {
        // 250/page is Shopify's max; loop for collections larger than that,
        // capped to keep a runaway collection from firing unbounded requests.
        for (let page = 1; page <= 8; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetch(`${this.productsJsonUrl}?limit=250&page=${page}`);
          if (!response.ok) break;
          // eslint-disable-next-line no-await-in-loop
          const { products: batch = [] } = await response.json();
          products.push(...batch);
          if (batch.length < 250) break;
        }
      } catch (error) {
        return null;
      }

      return products;
    }

    /**
     * Reads the currently active filter selections from the form, grouped by
     * their param name (e.g. 'filter.v.option.color': ['Red']).
     */
    getActiveFilterParams() {
      const byParam = {};

      new FormData(this.form).forEach((rawValue, name) => {
        if (!rawValue || !name.startsWith('filter.')) return;
        if (!byParam[name]) byParam[name] = [];
        byParam[name].push(rawValue);
      });

      return byParam;
    }

    /**
     * Whether the product matches every OTHER currently active filter
     * (excluding excludeParam's own group, since values within the same
     * filter are OR'd together, not AND'd). Supports variant options,
     * product type, vendor, tags and price range - the fields products.json
     * exposes. Metafield-based filters (filter.p.m.*) can't be verified from
     * that payload, so they're left unenforced here rather than silently
     * excluding products that would actually match.
     */
    productMatchesOtherFilters(product, activeByParam, excludeParam) {
      const matchesGroups = Object.keys(activeByParam).every((param) => {
        if (param === excludeParam || param === 'filter.v.price.gte' || param === 'filter.v.price.lte') return true;

        if (param.startsWith('filter.v.option.')) {
          const values = activeByParam[param];
          return product.variants.some((variant) => (
            variant.available && values.some((value) => [variant.option1, variant.option2, variant.option3].includes(value))
          ));
        }

        if (param === 'filter.p.product_type') return activeByParam[param].includes(product.product_type);
        if (param === 'filter.p.vendor') return activeByParam[param].includes(product.vendor);
        if (param === 'filter.p.tag') return activeByParam[param].some((tag) => (product.tags || []).includes(tag));

        return true;
      });

      return matchesGroups && this.productMatchesPriceRange(product, activeByParam);
    }

    productMatchesPriceRange(product, activeByParam) {
      const gte = activeByParam['filter.v.price.gte'] ? parseFloat(activeByParam['filter.v.price.gte'][0]) : null;
      const lte = activeByParam['filter.v.price.lte'] ? parseFloat(activeByParam['filter.v.price.lte'][0]) : null;
      if (gte === null && lte === null) return true;

      return product.variants.some((variant) => {
        const price = parseFloat(variant.price);
        if (gte !== null && price < gte) return false;
        if (lte !== null && price > lte) return false;
        return true;
      });
    }

    /**
     * Updates the result count.
     * @param {string} html - HTML of the fetched page.
     */
    updateResultsTotal(html) {
      if (this.resultsCountEl) {
        this.resultsCountEl.innerHTML = html.querySelector('#results-count').innerHTML;
      }
    }

    /**
     * Updates the active filters.
     * @param {string} html - HTML of the fetched page.
     */
    updateActiveFilters(html) {
      this.activeFilters.innerHTML = html.querySelector('.active-filters').innerHTML;
      this.activeFilters.hidden = !this.activeFilters.querySelector('.active-filters__item');
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
